'use client';
import { usePathname, redirect, useSearchParams } from 'next/navigation';

import LinkGenerator from '@nerdwallet/nw-auth-links';
import { useFeatureFlag } from '@nerdwallet/features';
import { getAuth0Params } from '@lib/auth0-redirect';
import { FEATURE_FLAGS } from '@constants/feature-flags';
import Paths from '@lib/paths';

const authRedirectOverrides = [
  Paths.NERDWALLET_PLUS_CHECKOUT,
  Paths.INVESTMENT_ACCOUNTS,
  Paths.NERDWALLET_PLUS_INVESTMENT_ACCOUNTS,
];

const getCancelUrl = (currentPath: string, fallback = Paths.DASHBOARD_HOME) => {
  const { pathname } = new URL(currentPath);
  const doesCurrentPathMatch = authRedirectOverrides.includes(pathname);
  const referrer = document?.referrer;

  let cancelPath = doesCurrentPathMatch ? referrer : pathname;

  // fallback in case a user manually navigates to currentPath
  if (
    (!cancelPath && doesCurrentPathMatch) ||
    authRedirectOverrides.includes(cancelPath)
  ) {
    cancelPath = fallback;
  }
  return cancelPath;
};

export const LoginRedirectHandler = () => {
  const path = `${process.env.NEXT_PUBLIC_INTERNAL_BASE_URL}` + usePathname();
  const { data: onboardingAuthFeatureFlagData, loading } = useFeatureFlag({
    featureFlagName: FEATURE_FLAGS.NW_PLUS_ONBOARDING_AUTH,
  });
  const { data: loginRedirectHandlerData, loading: isCancelPathLoading } =
    useFeatureFlag({
      featureFlagName: FEATURE_FLAGS.LOGIN_REDIRECT_HANDLER_LOGIC,
    });
  const qp = useSearchParams();

  if (loading || isCancelPathLoading) return null;

  const isInvestmentAccountsPath =
    path.endsWith(Paths.INVESTMENT_ACCOUNTS) ||
    path.endsWith(Paths.NERDWALLET_PLUS_INVESTMENT_ACCOUNTS);
  const isCheckoutPath = path.endsWith(Paths.NERDWALLET_PLUS_CHECKOUT);
  const useOnboardingAuthParams =
    onboardingAuthFeatureFlagData?.value &&
    (isCheckoutPath || isInvestmentAccountsPath);
  const onboardingAuthParams = useOnboardingAuthParams
    ? onboardingAuthFeatureFlagData.extraData
    : {};

  const isCancelUrlFFActive = loginRedirectHandlerData?.value;
  const referrer = document?.referrer;
  let cancelPath = isCheckoutPath ? referrer : path;
  if (isCancelUrlFFActive) {
    cancelPath = getCancelUrl(path);
  } else if (
    (!cancelPath && isCheckoutPath) ||
    cancelPath.endsWith(Paths.NERDWALLET_PLUS_CHECKOUT)
  ) {
    cancelPath = Paths.DASHBOARD_HOME;
  }

  const auth0Params = {
    ...getAuth0Params({
      search: `?redirectTo=${encodeURIComponent(path + `${qp.toString() ? `?${qp.toString()}` : ''}`)}`,
    }),
    cancelUrl: cancelPath,
    mfa: !isCheckoutPath, // Delay MFA for checkout path
    ...(qp.get('driverPageUrl') && {
      driverPageUrl: `${process.env.NEXT_PUBLIC_INTERNAL_BASE_URL}${qp.get('driverPageUrl')}`,
    }),
    ...(qp.get('redirect_uri') &&
      !isCheckoutPath && {
        redirectTo: `${process.env.NEXT_PUBLIC_INTERNAL_BASE_URL}${qp.get('redirect_uri')}`,
      }),
    ...onboardingAuthParams,
    ...(qp.get('fbclid') && {
      fbclid: qp.get('fbclid'),
    }),
  };
  const linkGenerator = new LinkGenerator();
  const redirectPath =
    isCheckoutPath || isInvestmentAccountsPath
      ? linkGenerator.register(auth0Params)
      : linkGenerator.login(auth0Params);
  redirect(redirectPath);
};
